import React, { useEffect, useRef, useState } from 'react';
import { Send, Paperclip, X, FileText, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * SmartInput — composer with attached-file chips and quick-action buttons.
 *
 * Designed for the /api/v2/agent flow. Keeps the public API small so it
 * can also be reused inside the legacy ChatInput later.
 *
 * Props
 *   onSubmit          — (message: string, files: File[], action?: string) => void
 *   attachedFiles     — File[] (controlled by parent)
 *   onRemoveFile      — (file: File) => void
 *   onAttachFiles     — (files: File[]) => void
 *   suggestedActions  — Array<{ id: string, label_ar: string, label_en: string }>
 *   showImproveButton — boolean (true once a response exists)
 *   onImprove         — () => void
 *   isSubmitting      — boolean (disables submit)
 *   placeholder       — string override
 */
const DEFAULT_ACTIONS = [
  { id: 'review',     label_ar: 'مراجعة',           label_en: 'Review' },
  { id: 'draft',      label_ar: 'صياغة',            label_en: 'Draft' },
  { id: 'compliance', label_ar: 'تحليل الامتثال',   label_en: 'Compliance' },
];

const MAX_LINES_PX = 220; // ~10 lines

export const SmartInput = ({
  onSubmit,
  attachedFiles = [],
  onRemoveFile,
  onAttachFiles,
  suggestedActions = DEFAULT_ACTIONS,
  showImproveButton = false,
  onImprove,
  isSubmitting = false,
  placeholder,
}) => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';

  const [value, setValue] = useState('');
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const placeholderText = placeholder
    || (isArabic ? 'اطرح سؤالك القانوني أو ارفع مستنداً...' : 'Ask a legal question or attach a document...');

  // Auto-grow
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, MAX_LINES_PX)}px`;
  }, [value]);

  // Receive prefill text broadcast from the TemplatesPanel.
  useEffect(() => {
    const onPrefill = (e) => {
      const t = e.detail?.text;
      if (typeof t === 'string') {
        setValue(t);
        requestAnimationFrame(() => textareaRef.current?.focus());
      }
    };
    window.addEventListener('bayenah:composer-prefill', onPrefill);
    return () => window.removeEventListener('bayenah:composer-prefill', onPrefill);
  }, []);

  const submit = (action) => {
    const text = value.trim();
    // Quick-action clicks (e.g. Compliance, Review, Draft) must always
    // dispatch — the parent page reads `documentText` from session
    // context (vault doc, uploaded file, or right-pane editor) so the
    // composer's textarea being empty is a valid state.  Without this
    // exemption the Compliance button silently no-ops when the user
    // has a document loaded but hasn't typed anything.
    if (!action && !text && !attachedFiles.length) return;
    if (isSubmitting) return;
    onSubmit?.(text, attachedFiles, action || null);
    setValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) onAttachFiles?.(files);
    e.target.value = ''; // allow re-selecting same file
  };

  return (
    <div
      className={cn(
        'rounded-lg border border-[#E8E8E8] bg-[#F5F5F3] p-3',
        'focus-within:border-[#1A1A1A]/30 transition-colors',
        'min-h-[80px] flex flex-col',
        isArabic && 'font-arabic',
      )}
      data-testid="smart-input-root"
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {/* File chips */}
      {attachedFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid="smart-input-files">
          {attachedFiles.map((f, idx) => (
            <span
              key={`${f.name}-${idx}`}
              className="inline-flex items-center gap-2 max-w-[260px] rounded-full bg-[#F5F5F3] border border-black/10 px-3 py-1 text-xs text-[#1A1A1A]"
              data-testid={`smart-input-file-chip-${idx}`}
            >
              <FileText className="w-3 h-3 text-[#7C5E3C] shrink-0" />
              <span className="truncate">{f.name}</span>
              <button
                type="button"
                onClick={() => onRemoveFile?.(f)}
                className="text-[#999999] hover:text-[#1A1A1A] shrink-0"
                aria-label="Remove file"
                data-testid={`smart-input-remove-file-${idx}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <textarea
        ref={textareaRef}
        rows={2}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholderText}
        disabled={isSubmitting}
        data-testid="smart-input-textarea"
        className={cn(
          'w-full bg-transparent border-0 outline-none resize-none flex-1',
          'text-base md:text-[15px] leading-relaxed text-[#1A1A1A]',
          'placeholder:text-[#999999]',
          isArabic && 'text-right',
        )}
      />

      {/* Action row */}
      <div className={cn('mt-2 flex items-center gap-2 flex-wrap', isArabic && 'flex-row-reverse')}>
        {/* Attach */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-black/5 text-[#1A1A1A]/70"
          data-testid="smart-input-attach-button"
          aria-label="Attach file"
        >
          <Paperclip className="w-4 h-4" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFiles}
          data-testid="smart-input-file-input"
        />

        {/* Quick actions — neutral chips (NOT primary). Spec P1#7:
            inactive filter = white bg + 1px gray border. */}
        {suggestedActions.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => submit(a.id)}
            disabled={isSubmitting}
            className={cn(
              'rounded-full border border-[#E8E8E8] bg-white px-3 py-1 text-sm text-[#1A1A1A]',
              'hover:bg-[#F5F5F3] transition-colors',
              isSubmitting && 'opacity-50 cursor-not-allowed',
            )}
            data-testid={`smart-input-quick-action-${a.id}`}
          >
            {isArabic ? a.label_ar : a.label_en}
          </button>
        ))}

        {showImproveButton && (
          <button
            type="button"
            onClick={onImprove}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E8E8E8] bg-white px-3 py-1 text-sm hover:bg-[#F5F5F3]"
            data-testid="smart-input-improve-button"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>{isArabic ? 'تحسين' : 'Improve'}</span>
          </button>
        )}

        {/* Spacer + submit */}
        <div className="flex-1" />

        <button
          type="button"
          onClick={() => submit()}
          disabled={isSubmitting || (!value.trim() && !attachedFiles.length)}
          className={cn(
            'inline-flex items-center justify-center gap-1.5 transition-colors font-arabic',
            'bg-[#111111] text-white hover:bg-black/85',
            'disabled:bg-[#D4D4D4] disabled:cursor-not-allowed',
          )}
          style={{
            height: '32px',
            padding: '0 14px',
            fontSize: '13px',
            borderRadius: '9999px',
          }}
          aria-label="Send"
          data-testid="smart-input-submit-button"
        >
          <span>{isArabic ? 'اسأل بيّنة' : 'Ask Bayenah'}</span>
          <Send className={cn('w-3.5 h-3.5', isArabic && 'scale-x-[-1]')} />
        </button>
      </div>
    </div>
  );
};

export default SmartInput;
